import { useState, useRef, useEffect, useCallback } from 'react';
import { X, Bug, Lightbulb, Send, Loader2, AlertCircle, Camera, ImagePlus, Trash2, ClipboardPaste } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES } from '../lib/auditLog';
import { logEvent } from '../lib/telemetry';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPage?: string;
  prefill?: {
    type: 'bug' | 'feature';
    title?: string;
    description?: string;
    errorDetails?: Record<string, unknown>;
  };
}

interface FeedbackRow {
  id: string;
}

const MAX_SCREENSHOTS = 5;

/**
 * Pasted clipboard images often arrive as a File named "image.png" or with an
 * empty name. Give them a stable, extension-bearing name so the upload path
 * (which derives the blob extension from the filename) always has one.
 */
function namedImage(f: File): File {
  if (f.name && f.name.includes('.')) return f;
  const type = f.type || 'image/png';
  const ext = type.split('/')[1] || 'png';
  return new File([f], `screenshot-${Date.now()}.${ext}`, { type });
}

export function FeedbackModal({ isOpen, onClose, currentPage, prefill }: FeedbackModalProps) {
  const { t } = useTranslation('common');
  const [type, setType] = useState<'bug' | 'feature'>(prefill?.type || 'bug');
  const [title, setTitle] = useState(prefill?.title || '');
  const [description, setDescription] = useState(prefill?.description || '');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [screenshots, setScreenshots] = useState<{ file: File; preview: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  /**
   * How many screenshots couldn't be uploaded. The report itself is submitted
   * first and never fails because of a screenshot (see handleSubmit), but we say
   * so on the success screen rather than pretending everything attached.
   */
  const [failedScreenshots, setFailedScreenshots] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  // Funnel every add path (pick / camera / paste / drop) through one place so the
  // image-only filter, the cap, and the object-URL previews stay consistent.
  const addFiles = useCallback((files: File[]) => {
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) return;
    const mapped = images.map((file) => ({ file, preview: URL.createObjectURL(file) }));
    setScreenshots((prev) => [...prev, ...mapped].slice(0, MAX_SCREENSHOTS));
  }, []);

  // Paste a screenshot from the clipboard (Ctrl/Cmd+V) anywhere in the modal. A
  // document-level listener (active only while the modal is open) is used rather
  // than binding to one element, because the buttons/inputs steal focus — the
  // pattern proven in ProjectAtlas-Roster's AttachmentUploader. Reads both
  // clipboardData.items AND .files (screenshot tools like Greenshot populate one
  // or the other); only image pastes preventDefault, so normal text paste into
  // the title/description still works.
  useEffect(() => {
    if (!isOpen) return;
    const onPaste = (e: ClipboardEvent) => {
      const cd = e.clipboardData;
      if (!cd) return;
      const imgs: File[] = [];
      for (const item of Array.from(cd.items)) {
        if (item.kind === 'file' && item.type.startsWith('image/')) {
          const f = item.getAsFile();
          if (f) imgs.push(namedImage(f));
        }
      }
      if (imgs.length === 0 && cd.files.length > 0) {
        for (const f of Array.from(cd.files)) {
          if (f.type.startsWith('image/')) imgs.push(namedImage(f));
        }
      }
      if (imgs.length === 0) return; // not an image → let normal text paste proceed
      e.preventDefault();
      addFiles(imgs);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [isOpen, addFiles]);

  if (!isOpen) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
  };

  const removeScreenshot = (index: number) => {
    setScreenshots((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  /**
   * Upload each screenshot, tolerating individual failures. A screenshot is an
   * attachment to the report, never a precondition for it — if blob storage is
   * unreachable (or the server can't authenticate to it) the user's bug report
   * must still land, with the image quietly omitted. Returns the URLs that made
   * it plus how many didn't, so the success screen can be honest about it.
   */
  const uploadScreenshots = async (
    feedbackId: string,
  ): Promise<{ urls: string[]; failed: number }> => {
    const urls: string[] = [];
    let failed = 0;
    for (let i = 0; i < screenshots.length; i++) {
      const ss = screenshots[i];
      const ext = ss.file.name.split('.').pop() || 'jpg';
      const path = `${feedbackId}/${Date.now()}_${i}.${ext}`;
      try {
        const result = await apiClient.upload('feedback-screenshots', ss.file, {
          path,
          filename: ss.file.name,
        });
        urls.push(result.url);
      } catch (err) {
        failed += 1;
        console.error('Screenshot upload failed:', err);
        // Leave a breadcrumb — the upload path in apiClient doesn't log API
        // errors the way request() does, which is why the last occurrence of
        // this left no client-side trace at all. `origin` is recorded so a
        // future failure immediately shows whether it came from production or
        // from a developer's localhost server.
        void logEvent('feedback_screenshot_upload_failed', 'Screenshot upload failed', {
          severity: 'warning',
          metadata: {
            feedback_id: feedbackId,
            index: i,
            status: (err as { status?: number })?.status ?? 0,
            code: (err as { code?: string })?.code ?? 'unknown',
            message: err instanceof Error ? err.message : String(err),
            origin: typeof location !== 'undefined' ? location.origin : null,
          },
        });
      }
    }
    return { urls, failed };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      setError(t('feedback.validationError'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Server generates the id; insert first, then upload screenshots under
      // {id}/..., then PUT screenshot_urls back. Avoids dangling blobs if the
      // POST fails server-side validation.
      const created = await apiClient.post<FeedbackRow>('/api/feedback', {
        type,
        title: title.trim(),
        description: description.trim(),
        priority,
        page_context: currentPage || '',
        user_agent: navigator.userAgent,
        error_details: prefill?.errorDetails || null,
        screenshot_urls: [],
      });

      let screenshotUrls: string[] = [];
      let screenshotFailures = 0;
      if (screenshots.length > 0) {
        // uploadScreenshots never rejects — it swallows each file's failure and
        // reports a count. Past this line the report already exists in the DB
        // and holds the user's words, so nothing here may surface as a submit
        // error and send them back to a form they'd have to re-fill.
        const uploaded = await uploadScreenshots(created.id);
        screenshotUrls = uploaded.urls;
        screenshotFailures = uploaded.failed;
        if (screenshotUrls.length > 0) {
          // PUT to attach URLs. If this fails the row exists with empty URLs;
          // an admin can still clean up. Better than failing the whole submit.
          try {
            await apiClient.put(`/api/feedback/${created.id}`, { screenshot_urls: screenshotUrls });
          } catch (urlErr) {
            screenshotFailures += screenshotUrls.length;
            console.error('Failed to attach screenshot URLs:', urlErr);
          }
        }
      }
      setFailedScreenshots(screenshotFailures);

      // Audit is bookkeeping, not part of the submission. The row is already in
      // the DB; a failed audit write must not turn a successful report into an
      // "Error submitting" message that makes the user try again.
      try {
        await logAudit({
          action: AUDIT_ACTIONS.CREATE,
          entityType: AUDIT_ENTITIES.FEEDBACK,
          entityId: created.id,
          entityLabel: title.trim(),
          details: { type, priority },
          pageContext: currentPage || 'feedback',
        });
      } catch (auditErr) {
        console.error('Failed to write feedback audit entry:', auditErr);
      }
      setSuccess(true);
      // Linger a little longer when there's a "screenshot didn't attach" note to read.
      setTimeout(() => {
        resetForm();
        onClose();
      }, screenshotFailures > 0 ? 4500 : 1500);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError(t('feedback.submitError'));
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setType('bug');
    setTitle('');
    setDescription('');
    setPriority('medium');
    screenshots.forEach((ss) => URL.revokeObjectURL(ss.preview));
    setScreenshots([]);
    setError(null);
    setSuccess(false);
    setFailedScreenshots(0);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const priorityKeys = ['low', 'medium', 'high', 'critical'] as const;
  const priorityColors: Record<string, string> = {
    low: 'bg-gray-100 text-gray-700 border-gray-300', // theme-allow: neutral priority pill (part of priority color map)
    medium: 'bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/30',
    high: 'bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30',
    critical: 'bg-red-50 text-red-700 border-red-300 dark:bg-red-500/10 dark:text-red-300 dark:border-red-500/30',
  };
  const prioritySelectedColors: Record<string, string> = {
    low: 'bg-gray-200 text-gray-800 border-gray-500 ring-2 ring-gray-300', // theme-allow: neutral selected priority pill (part of priority color map)
    medium: 'bg-blue-100 text-blue-800 border-blue-500 ring-2 ring-blue-200 dark:bg-blue-500/20 dark:text-blue-300',
    high: 'bg-amber-100 text-amber-800 border-amber-500 ring-2 ring-amber-200 dark:bg-amber-500/20 dark:text-amber-300',
    critical: 'bg-red-100 text-red-800 border-red-500 ring-2 ring-red-200 dark:bg-red-500/20 dark:text-red-300',
  };
  const priorityLabels: Record<string, string> = {
    low: t('feedback.priorityLow'),
    medium: t('feedback.priorityMedium'),
    high: t('feedback.priorityHigh'),
    critical: t('feedback.priorityCritical'),
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-line">
          <h2 className="text-lg font-semibold text-theme-primary">{t('feedback.modalTitle')}</h2>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg text-theme-secondary hover:bg-subtle transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {success ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-green-500/20">
              <Send className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-theme-primary mb-2">{t('feedback.successTitle')}</h3>
            <p className="text-theme-secondary">{t('feedback.successMessage')}</p>
            {failedScreenshots > 0 && (
              <div className="mt-4 flex items-start gap-2 text-left rounded-lg border px-3 py-2 bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-300">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <p className="text-xs">
                  {t(
                    'feedback.screenshotUploadFailed',
                    "Your report was sent, but the screenshot couldn't be attached. You can add it later or reply with it.",
                  )}
                </p>
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
            <div className="overflow-y-auto px-6 py-4 space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setType('bug')}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
                    type === 'bug'
                      ? 'border-red-400 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300'
                      : 'border-line bg-surface text-theme-secondary hover:border-line hover:bg-subtle'
                  }`}
                >
                  <Bug size={20} className={type === 'bug' ? 'text-red-500' : 'text-theme-secondary'} />
                  <div className="text-left">
                    <p className="font-medium text-sm">{t('feedback.typeBug')}</p>
                    <p className="text-xs opacity-70">{t('feedback.typeBugDesc')}</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setType('feature')}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all ${
                    type === 'feature'
                      ? 'border-teal-400 bg-teal-50 text-teal-800 dark:border-teal-500/30 dark:bg-teal-500/10 dark:text-teal-300'
                      : 'border-line bg-surface text-theme-secondary hover:border-line hover:bg-subtle'
                  }`}
                >
                  <Lightbulb size={20} className={type === 'feature' ? 'text-teal-500' : 'text-theme-secondary'} />
                  <div className="text-left">
                    <p className="font-medium text-sm">{t('feedback.typeFeature')}</p>
                    <p className="text-xs opacity-70">{t('feedback.typeFeatureDesc')}</p>
                  </div>
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1.5">{t('feedback.titleLabel')}</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={type === 'bug' ? t('feedback.titlePlaceholderBug') : t('feedback.titlePlaceholderFeature')}
                  className="w-full px-4 py-2.5 bg-surface text-theme-primary border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  maxLength={200}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-theme-primary mb-1.5">{t('feedback.descriptionLabel')}</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={type === 'bug' ? t('feedback.descriptionPlaceholderBug') : t('feedback.descriptionPlaceholderFeature')}
                  rows={4}
                  className="w-full px-4 py-2.5 bg-surface text-theme-primary border border-line rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                />
              </div>

              {type === 'bug' && (
                <div>
                  <label className="block text-sm font-medium text-theme-primary mb-2">{t('feedback.priorityLabel')}</label>
                  <div className="flex gap-2">
                    {priorityKeys.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setPriority(p)}
                        className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-all ${
                          priority === p ? prioritySelectedColors[p] : priorityColors[p]
                        }`}
                      >
                        {priorityLabels[p]}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(Array.from(e.dataTransfer.files)); }}
                className={`rounded-lg transition-colors ${dragOver ? 'ring-2 ring-blue-400 ring-offset-2 ring-offset-surface' : ''}`}
              >
                <label className="block text-sm font-medium text-theme-primary mb-1">{t('feedback.screenshotsLabel')}</label>
                <p className="flex items-center gap-1.5 text-xs text-theme-secondary mb-2">
                  <ClipboardPaste size={13} className="shrink-0" />
                  {t('feedback.screenshotsHint')}
                </p>
                {screenshots.length > 0 && (
                  <div className="flex gap-2 flex-wrap mb-3">
                    {screenshots.map((ss, i) => (
                      <div key={i} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-line">
                        <img src={ss.preview} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeScreenshot(i)}
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        >
                          <Trash2 size={16} className="text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {screenshots.length < MAX_SCREENSHOTS && (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => cameraInputRef.current?.click()}
                      className="flex items-center gap-1.5 px-3 py-2 bg-surface border border-line rounded-lg text-xs font-medium text-theme-primary hover:bg-subtle transition-colors"
                    >
                      <Camera size={14} />
                      {t('feedback.camera')}
                    </button>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-1.5 px-3 py-2 bg-surface border border-line rounded-lg text-xs font-medium text-theme-primary hover:bg-subtle transition-colors"
                    >
                      <ImagePlus size={14} />
                      {t('feedback.gallery')}
                    </button>
                    <input
                      ref={cameraInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/gif"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </div>
                )}
              </div>

              {prefill?.errorDetails && (
                <div className="bg-subtle border border-line rounded-lg p-3">
                  <p className="text-xs font-medium text-theme-secondary mb-1">{t('feedback.errorDetailsCaptured')}</p>
                  <p className="text-xs text-theme-secondary font-mono truncate">
                    {(prefill.errorDetails as { message?: string }).message || t('feedback.runtimeError')}
                  </p>
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm dark:text-red-400">
                  <AlertCircle size={16} />
                  {error}
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-subtle rounded-b-xl flex items-center justify-end gap-3 border-t border-line">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="px-4 py-2 text-theme-primary bg-surface border border-line rounded-lg hover:bg-subtle transition-colors font-medium text-sm disabled:opacity-50"
              >
                {t('actions.cancel')}
              </button>
              <button
                type="submit"
                disabled={saving || !title.trim() || !description.trim()}
                className="px-5 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors font-medium text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {t('feedback.submitting')}
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    {t('actions.submit')}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
